'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import {
  IMarketChartContextData,
} from '@/creditflowresearch/domain/vo/MarketChart/IMarketChartContextData';
import MarketChartRepository
  from '@/creditflowresearch/infrastructure/repository/MarketChartRepository/MarketChartRepository';
import { getContainer } from '@framework/di/getContainer';
import { useRouter } from 'next/router';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { selectProductAndRegionIdsFromQuery } from '@/creditflowresearch/application/PageFilters/selectProductAndRegionIdsFromQuery';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import {
  initialMarketChartResponse,
} from '@/creditflowresearch/application/MarketChart/constants';
import {
  filterEmptyDeals,
} from '@/creditflowresearch/application/MarketChart/serverSide/helpers';

const useMarketChart = (data) => {
  const [chartData, setChartData] = useState<IMarketChartContextData>(data);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isFirstFetch, setIsFirstFetch] = useState<boolean>(true);
  const router = useRouter();

  const { regions: regionsList, products: productsList } = useHeaderMenuContext();

  const { plan } = useUserPlanContext();

  const container = getContainer();
  const marketChartRepository = container.get(MarketChartRepository);

  const preparePayload = () => {
    const { pathname, query } = router;

    const { productIds, regionIds } = selectProductAndRegionIdsFromQuery({
      pathname,
      query,
      plan,
      regionsList,
      productsList,
    });

    const payload = {
      filters: {
        includedProductIds: productIds,
        includedRegionIds: regionIds,
      },
    };

    return {
      ...data.preparedPayload,
      filters: {
        ...data.preparedPayload.filters,
        ...payload.filters,
      },
    };
  };

  const getMarketChartData = async (filters) => {
    setIsFetching(true);
    try {
      const { chartItems, showVolume } = await marketChartRepository.getChartData(filters);
      setChartData({
        collection: filterEmptyDeals(chartItems), showVolume,
      });
    } catch (e) {
      setChartData(initialMarketChartResponse);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    setChartData(data);
  }, [data]);

  useEffect(() => {
    if (!isFirstFetch) {
      const payload = preparePayload();
      getMarketChartData(payload);
    }
  }, [router.query]);

  useEffect(() => {
    setIsFirstFetch(false);
  }, []);

  return {
    chartData: { ...chartData, isFetching },
    setChartData,
  };
};

type MarketChartContextType = ReturnType<typeof useMarketChart>;

interface IMarketChartContextProviderProps {
  children: React.ReactNode,
  chartData: IMarketChartContextData,
}

const MarketChartContext = createContext<MarketChartContextType>(null);

export const useMarketChartContext = () => {
  const context = useContext(MarketChartContext);
  if (!context) {
    throw new Error('useMarketChartContext must be used within the MarketChartContext');
  }
  return context;
};

const MarketChartContextProvider = ({ children, chartData }: IMarketChartContextProviderProps) => (
  <MarketChartContext.Provider value={ useMarketChart(chartData) }>
    { children }
  </MarketChartContext.Provider>
);

export default MarketChartContextProvider;
